import { ApplicationIcon, Icon, Typography } from "@saviynt/design-system";
import type { CSSProperties, ReactNode } from "react";
import { FormattedMessage } from "react-intl";
import { getIntegrationTypeMeta } from "../../config/integrationTypeConfig";
import type { IntegrationType } from "./IntegrationTypeSelector";
import "./IntegrationTypeCard.css";

interface IntegrationTypeCardProps {
  type: IntegrationType;
  onSelect: (typeName: string) => void;
  /** Search query for match highlighting in name + description. */
  searchQuery?: string;
  /** Index used to compute staggered entrance animation delay. */
  index?: number;
}

/** Single tile color for every integration card. */
const TILE_COLOR = "savi" as const;

function escapeRegExp(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** Wrap substrings matching `query` (case-insensitive) in <mark>. */
function highlightMatch(text: string, query: string): ReactNode {
  const q = query.trim();
  if (!q) return text;
  const regex = new RegExp(`(${escapeRegExp(q)})`, "gi");
  const parts = text.split(regex);
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <mark key={i} className="IntegrationTypeCard__highlight">
        {part}
      </mark>
    ) : (
      part
    ),
  );
}

export function IntegrationTypeCard({
  type,
  onSelect,
  searchQuery = "",
  index = 0,
}: IntegrationTypeCardProps) {
  const meta = getIntegrationTypeMeta(type.typeName);
  const displayName = type.displayName;
  const logo = meta?.icon ?? type.typeName;
  const description = type.description || meta?.description || "";
  const tileColor = TILE_COLOR;

  const style: CSSProperties = {
    animationDelay: `${Math.min(index, 20) * 30}ms`,
  };

  return (
    <button
      type="button"
      onClick={() => onSelect(type.typeName)}
      style={style}
      className={`IntegrationTypeCard IntegrationTypeCard--${tileColor} group relative flex flex-col items-start text-left w-full min-h-[168px] cursor-pointer rounded-[var(--border-radius-md,12px)] border p-5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-border-focus,#0073e6)]`}
    >
      <div className="flex w-full items-center gap-4">
        <div
          className={`IntegrationTypeCard__tile IntegrationTypeCard__tile--${tileColor} flex items-center justify-center w-14 h-14 overflow-hidden rounded-[var(--border-radius-sm,8px)] shrink-0`}
          data-logo={logo}
          aria-hidden="true"
        >
          <ApplicationIcon logo={logo} size="medium" />
        </div>
        <div className="flex-1 min-w-0">
          <Typography
            kind="h4"
            className="IntegrationTypeCard__name font-semibold tracking-[-0.005em] text-[var(--color-foreground-neutral-bold,#0d0f1c)]"
          >
            {highlightMatch(displayName, searchQuery)}
          </Typography>
        </div>
      </div>
      {description && (
        <p
          className="IntegrationTypeCard__description pt-2 m-0 line-clamp-2 text-[13px] leading-5 text-[var(--color-foreground-neutral-medium,#4a5568)]"
          title={description}
        >
          {highlightMatch(description, searchQuery)}
        </p>
      )}
      <span className="IntegrationTypeCard__connect absolute right-4 bottom-3.5 inline-flex items-center gap-1 text-[13px] font-medium opacity-0 -translate-x-1 pointer-events-none transition-[opacity,transform] duration-200 ease-out group-hover:opacity-100 group-hover:translate-x-0 group-focus-visible:opacity-100 group-focus-visible:translate-x-0">
        <FormattedMessage
          id="RZcZ9t"
          defaultMessage="Connect"
          description="Hover CTA label on integration type card"
        />
        <Icon kind="ArrowRight" size="small" />
      </span>
    </button>
  );
}
